import Reflux from "reflux";
import Immutable, { Map } from "immutable";
import _ from "lodash";
import { mat2d, vec2, glMatrix } from "gl-matrix";

import IDUtils from "../utils/IDUtils";
import AppConstants from "../constants/AppConstants";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import TransformActions from "../actions/TransformActions";
import SelectionStore from "./SelectionStore";
import BlockUpdateVO from "../vo/BlockUpdate.mjs";
import { gatherProps } from "../utils/Merge/MergeUtils.mjs";
import PROPERTY_MAPS from "../constants/PropertyMaps";
import ActiveBlockContainerStore from "./ActiveBlockContainerStore.mjs";
import ElementUtils from "@/utils/ElementUtils";
import { gatherUnchangedGroupPositions, redirectMove, resizeContainer } from "@/utils/GroupUtils";
import DataTypes from "@/constants/DataTypes";
import { ELEMENTS } from "@/constants/EntityConstants";
import GroupActions from "@/actions/GroupActions";
import { MERGE } from "@/constants/PropUpdateConstants.mjs";
import {
  gatherDimensionPositions,
  getDimensionPosition,
} from "@/utils/Element/SizeAndPositionUtils";

const { ELEMENT_POSITION } = PROPERTY_MAPS;

glMatrix.setMatrixArrayType(Array);

/**
 * @typedef {import("@/types/TransformerTypes.mjs").ElementInfo} ElementInfo
 * @typedef {import("@/types/TransformerTypes.mjs").TransformDiff} TransformDiff
 * @typedef {import("@/types/ElementTypes.mjs").DimensionPosition} DimensionPosition
 *
 * @typedef {import("@/types/TypeUtils.mjs").ImmutableMapFrom<Record<string, DimensionPosition>>} PositionUpdates
 */

const TransformerStore = Reflux.createStore({
  storeName: 'TransformerStore',
  listenables: [TransformActions],

  init() {
    this.listenTo(SelectionStore, this.onSelectionChange);

    this.elements = Immutable.Map();
    /** @type {import("@/types/TypeUtils.mjs").ImmutableMapFrom<{[key: string]: ElementInfo}>} */
    this.positions = Immutable.Map();
    this.block = Immutable.Map();
    this.layout = null;
    this.transforming = false;
  },

  onSelectionChange() {
    this.block = SelectionStore.getBlock();
    this.layout = SelectionStore.getLayout();
    this.elements = SelectionStore.getElements();

    if (this.elements?.size === 0) {
      this.positions = this.positions.clear();
      this.transforming = false;
    }
  },

  /**
   * Applies diff to a target. Mutates the target.
   * @param {ElementInfo} target
   * @param {TransformDiff} diff
   * @returns {ElementInfo}
   */
  _applyDiff(target, diff) {
    target.x = target.iX + diff.xDiff;
    target.y = target.iY + diff.yDiff;
    target.w = target.iW + diff.wDiff;
    target.h = target.iH + diff.hDiff;
    target.a = diff.a;
    return target;
  },

  /**
   * Update current positions using diffs applied to initial positions.
   * Applies to contained elements if an updated position represents an element container.
   * @param {{ [key: string]: TransformDiff }} data
   */
  onTransform(data) {
    this.positions = this.positions.map((elemInfo, id) => {
      if (data[id]) {
        const diff = data[id];
        this._applyDiff(elemInfo, diff);

        // update contained elements if element is an element container
        if (diff.contained && elemInfo.contained) {
          elemInfo.contained = Object.entries(elemInfo.contained).reduce(
            (result, [containedId, containedInfo]) => {
              const containedDiff = diff.contained[containedId];
              result[containedId] = this._applyDiff(containedInfo, containedDiff);
              return result;
            },
            {}
          );
        }
      }
      return elemInfo;
    });

    this.trigger();
    if (!this.transforming) {
      this.transforming = true;
    }
  },

  /**
   * Generates updates based on end positions. Applies to contained elements if appropriate.
   * @param {*} data
   */
  onEndTransform(data) {
    const update = new BlockUpdateVO();
    const updatedPositions = gatherDimensionPositions(this.positions);

    // gather container dimensions and id if there is one
    const [containerId, container] = this.positions.reduce(
      (result, data) => (!result[0] ? [data.containerId, data.container] : result),
      []
    );

    // if element is selected and contained
    if (container && containerId) {
      const updatedElementIds = [...updatedPositions.keys()].map((id) => id.split(".").at(-1));
      const containerData = this.block.getIn(containerId.split(".").slice(1));

      const unchangedElementsPositions = gatherUnchangedGroupPositions(
        containerData,
        containerId,
        updatedElementIds,
        this.layout
      );

      const [resizedContainer, shiftedElements] = resizeContainer(
        updatedPositions.merge(unchangedElementsPositions).toJS(),
        container
      );

      update.add(containerId, this.layout, resizedContainer);
      Object.entries(shiftedElements).forEach(([id, value]) => update.add(id, this.layout, value));
      GroupActions.updateGroupData(resizedContainer, this.layout);
    } else {
      updatedPositions.forEach((data, id) => {
        update.add(id, this.layout, data, MERGE);

        const basePosition = this.positions.get(id);

        // update contained elements if parent container is transformed
        if (basePosition.contained) {
          Object.entries(basePosition.contained).forEach(([id, containedPosition]) => {
            update.add(id, this.layout, getDimensionPosition(containedPosition), MERGE);
          });
        }
      });
    }

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
    this.transforming = false;
  },

  onStartTransform(data) {
    if (this.elements.size > 0) {
      this.initPositions();
      this.transforming = true;
    }
  },

  _gatherContainer() {
    const transformableElements = this.getTransformableElements();
    const containerId = transformableElements.find((element) => !!element.containedBy)?.containedBy;
    const container = containerId ? this.block.getIn(IDUtils.getArray(containerId, 1)) : null;
    const containerDimensions = container
      ? getDimensionPosition(container.get(this.layout).toJS())
      : null;

    return containerId && container && containerDimensions
      ? {
          container,
          containerDimensions,
          containerId,
        }
      : {};
  },

  /**
   * Resizes a container based on updates if applicable, applies provided updates to blockUpdate.
   * The resize is accomplished by grabbing positions for unchanged elements along with current changed elements, and recalculates bounds.
   * Also emits new size to GroupStore if a resize does happen.
   * @param {*} props
   */
  _applyAndResize({ container, containerId, containerDimensions, updates, update }) {
    const { layout } = this;
    // update container and shifted positions if targets are contained
    if (container) {
      const updatedElementIds = [...updates.keys()].map((id) => id.split(".").at(-1));
      const updatedPositions = updates.map((positionUpdate, id) => {
        const elemIdPath = IDUtils.getArray(id, 1);
        const elem = this.block.getIn(elemIdPath);
        const dimensions = getDimensionPosition(elem.get(layout).toJS());
        return { ...dimensions, ...positionUpdate };
      });

      const unchangedElementsPositions = gatherUnchangedGroupPositions(
        container,
        containerId,
        updatedElementIds,
        layout
      );

      const [resizedContainer, shiftedElements] = resizeContainer(
        updatedPositions.merge(unchangedElementsPositions).toJS(),
        containerDimensions
      );

      update.add(containerId, this.layout, resizedContainer);
      Object.entries(shiftedElements).forEach(([id, value]) => update.add(id, this.layout, value));
      GroupActions.updateGroupData(resizedContainer, this.layout);
    } else {
      updates.forEach((propUpdate, id) => update.add(id, layout, propUpdate, MERGE));
    }
  },

  /**
   * Returns stored position for element at an id, or null when the transform snapshot doesn't track it.
   * @param {string} elementId
   * @param {boolean} [asImmutable]
   * @returns {object | Map | null}
   */
  getElementPosition(elementId, asImmutable) {
    const foundPosition =
      this.positions.get(elementId) ??
      this.positions.map((position) => position.contained?.[elementId]).find(Boolean);

    if (!foundPosition) return null;

    const { contained, ...position } = foundPosition;

    return asImmutable ? Map(position) : position;
  },

  getBounds() {
    const transformableElements = this.getTransformableElements();
    const rects = this.positions.size
      ? this.positions.map(this.getElementBoundingRectangle).toJS()
      : this.getElementRectangles(transformableElements);

    return this.getSelectionBoundingRectangle(rects);
  },

  onAlign(alignPos) {
    const transformableElements = this.getTransformableElements();
    const rects = this.getElementRectangles(transformableElements);
    const boundingRect = this.getSelectionBoundingRectangle(rects);
    const { container, containerDimensions, containerId } = this._gatherContainer();
  
    const propName =
      AppConstants.ALIGN_BOTTOM === alignPos ||
      AppConstants.ALIGN_TOP === alignPos ||
      AppConstants.ALIGN_MIDDLE === alignPos
        ? "y"
        : "x";
  
    const update = new BlockUpdateVO();
  
    const updates = Object.entries(rects).reduce((result, [id, position]) => {
      const propUpdate = {};
      propUpdate[propName] = _.round(
        position[propName] + (boundingRect[alignPos] - position[alignPos]),
        AppConstants.DECIMAL_PRECISION
      );
  
      const elemIdPath = IDUtils.getArray(id, 1);
      const elem = this.block.getIn(elemIdPath);
      const layoutProps = gatherProps(elem, this.layout, ELEMENT_POSITION, false).toJS();
  
      return result.set(id, getDimensionPosition({ ...layoutProps, ...propUpdate }));
    }, Map());
  
    this._applyAndResize({
      container,
      containerId,
      containerDimensions,
      updates,
      update,
    });
  
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  },

  onAlignToCanvas(alignPos) {
    const blockLayout = this.block.get(this.layout);
    const transformableElements = this.getTransformableElements();
    const rects = this.getElementRectangles(transformableElements);
    const boundingRect = this.getSelectionBoundingRectangle(rects);

    const propName =
      AppConstants.ALIGN_BOTTOM === alignPos ||
      AppConstants.ALIGN_TOP === alignPos ||
      AppConstants.ALIGN_MIDDLE === alignPos
        ? "y"
        : "x";

    const blockRect = {
      top: 0,
      middle: blockLayout.get("h") / 2,
      bottom: blockLayout.get("h"),
      left: 0,
      center: blockLayout.get("w") / 2,
      right: blockLayout.get("w"),
    };

    const update = new BlockUpdateVO();

    _.forEach(
      rects,
      _.bind(function (rect, id) {
        const propUpdate = {};
        propUpdate[propName] = _.round(
          rect[propName] + (blockRect[alignPos] - boundingRect[alignPos]),
          AppConstants.DECIMAL_PRECISION
        );
        update.add(id, this.layout, propUpdate);
      }, this)
    );

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  },

  onMatchSize(dimension) {
    if (this.elements.size === 0) {
      return;
    }
    let maxWidth = 0;
    let maxHeight = 0;

    const transformableElements = this.getTransformableElements();

    transformableElements.forEach(function (data, id) {
      const elem = this.block.getIn(IDUtils.getArray(id, 1));
      const isLine = data.type === AppConstants.ELEMENT_TYPE_LINE;

      const width = elem.getIn([this.layout, "w"]);
      const height = elem.getIn([this.layout, isLine ? "w" : "h"]);

      maxWidth = width > maxWidth ? width : maxWidth;
      maxHeight = height > maxHeight ? height : maxHeight;
    }, this);

    const update = new BlockUpdateVO();

    transformableElements.forEach(function (data, id) {
      const isLine = data.type === AppConstants.ELEMENT_TYPE_LINE;
      const propUpdate = {};
      if (
        dimension === AppConstants.MATCH_SIZE_WIDTH ||
        dimension === AppConstants.MATCH_SIZE_BOTH
      ) {
        propUpdate["w"] = _.round(maxWidth, AppConstants.DECIMAL_PRECISION);
      }

      if (
        dimension === AppConstants.MATCH_SIZE_HEIGHT ||
        dimension === AppConstants.MATCH_SIZE_BOTH
      ) {
        propUpdate[isLine && dimension !== AppConstants.MATCH_SIZE_BOTH ? "w" : "h"] = _.round(
          maxHeight,
          AppConstants.DECIMAL_PRECISION
        );
      }

      update.add(id, this.layout, propUpdate);
    }, this);

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  },

  onDistributeSpace(type) {
    const transformableElements = this.getTransformableElements();
    const rects = this.getElementRectangles(transformableElements);
    const boundingRect = this.getSelectionBoundingRectangle(rects);

    let totalWidth = 0;
    let totalHeight = 0;

    _.forEach(rects, function (rect, id) {
      totalWidth += rect.width;
      totalHeight += rect.height;
    });

    const numRects = _.size(rects);

    const verticalSpace = Math.max(boundingRect.height - totalHeight, 0) / (numRects - 1);
    const horizontalSpace = Math.max(boundingRect.width - totalWidth, 0) / (numRects - 1);

    let sortedRecs = null;

    if (AppConstants.DISTRIBUTE_VERTICAL_SPACE === type) {
      sortedRecs = _.sortBy(rects, "top");
    } else if (AppConstants.DISTRIBUTE_HORIZONTAL_SPACE === type) {
      sortedRecs = _.sortBy(rects, "left");
    }

    const update = new BlockUpdateVO();

    let prevRect = null;
    let currentRect = null;
    for (let i = 1; i < sortedRecs.length; i++) {
      prevRect = sortedRecs[i - 1];
      currentRect = sortedRecs[i];

      const propUpdate = {};
      if (AppConstants.DISTRIBUTE_VERTICAL_SPACE === type) {
        const vDiff = verticalSpace - (currentRect.top - prevRect.bottom);
        propUpdate["y"] = _.round(currentRect.y + vDiff, AppConstants.DECIMAL_PRECISION);
        currentRect.top += vDiff;
        currentRect.bottom += vDiff;
      } else if (AppConstants.DISTRIBUTE_HORIZONTAL_SPACE === type) {
        const hDiff = horizontalSpace - (currentRect.left - prevRect.right);
        propUpdate["x"] = _.round(currentRect.x + hDiff, AppConstants.DECIMAL_PRECISION);
        currentRect.left += hDiff;
        currentRect.right += hDiff;
      }

      update.add(currentRect.id, this.layout, propUpdate);
    }

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  },

  onShift(propName, amount) {
    if (this.elements.size === 0) {
      return;
    }

    const { layout } = this;
    const update = new BlockUpdateVO();
    const transformableElements = this.getTransformableElements();
    const { container, containerDimensions, containerId } = this._gatherContainer();

    const updateAmounts = {
      x: propName === "x" ? amount : 0,
      y: propName === "y" ? amount : 0,
    };

    // redirects to align back to stage orientation when contained
    if (containerDimensions) {
      const changeVec = [updateAmounts.x, updateAmounts.y];
      const redirected = redirectMove(changeVec, containerDimensions.a);
      updateAmounts.x = redirected[0];
      updateAmounts.y = redirected[1];
    }

    const updates = transformableElements.reduce((result, data, id) => {
      const elemIdPath = IDUtils.getArray(id, 1);
      const elem = this.block.getIn(elemIdPath);
      const layoutProps = elem.get(layout);

      // collect changed values for update
      const propUpdate = Object.entries(updateAmounts).reduce((reducedUpdates, [axis, value]) => {
        if (value) {
          const baseChange = layoutProps.get(axis) + value;

          reducedUpdates[axis] = containerId
            ? baseChange
            : _.round(baseChange, AppConstants.DECIMAL_PRECISION);
        }

        return reducedUpdates;
      }, {});

      return result.set(id, propUpdate);
    }, Map());

    this._applyAndResize({
      container,
      containerDimensions,
      containerId,
      updates,
      update,
    });

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  },

  getSelectionBoundingRectangle(elementRects) {
    var boundsRect = this._getNewRect();

    _.forEach(elementRects, function (rect, id) {
      if (boundsRect.top == null || rect.top < boundsRect.top) {
        boundsRect.top = rect.top;
      }

      if (boundsRect.bottom == null || rect.bottom > boundsRect.bottom) {
        boundsRect.bottom = rect.bottom;
      }

      if (boundsRect.left == null || rect.left < boundsRect.left) {
        boundsRect.left = rect.left;
      }

      if (boundsRect.right == null || rect.right > boundsRect.right) {
        boundsRect.right = rect.right;
      }
    });

    boundsRect.height = boundsRect.bottom - boundsRect.top;
    boundsRect.width = boundsRect.right - boundsRect.left;
    boundsRect.middle = boundsRect.top + boundsRect.height / 2;
    boundsRect.center = boundsRect.left + boundsRect.width / 2;

    return boundsRect;
  },

  getElementRectangles(elems) {
    return elems
      .map(({ id }) => {
        const elemIdPath = IDUtils.getArray(id, 1);
        const elem = this.block.getIn(elemIdPath);

        const layoutProps = gatherProps(elem, this.layout, ELEMENT_POSITION);

        return this.getElementBoundingRectangle(layoutProps, id);
      })
      .toJS();
  },

  getElementBoundingRectangle(layoutProps, id) {
    var props = Map.isMap(layoutProps) ? layoutProps.toJS() : layoutProps;
    var rect = this._getNewRect();
    rect.id = id;
    rect.top = props.y;
    rect.bottom = props.y + props.h;
    rect.left = props.x;
    rect.right = props.x + props.w;
    rect.width = props.w;
    rect.height = props.h;
    rect.x = props.x;
    rect.y = props.y;

    if (props.a != 0) {
      var matrix = mat2d.create();

      mat2d.translate(matrix, matrix, vec2.fromValues(props.x, props.y));
      mat2d.translate(matrix, matrix, vec2.fromValues(props.w / 2, props.h / 2));
      mat2d.rotate(matrix, matrix, (Math.PI / 180) * props.a);
      mat2d.translate(matrix, matrix, vec2.fromValues(-props.w / 2, -props.h / 2));
      mat2d.translate(matrix, matrix, vec2.fromValues(-props.x, -props.y));

      var tL = vec2.create();
      var tR = vec2.create();
      var bR = vec2.create();
      var bL = vec2.create();

      vec2.transformMat2d(tL, vec2.fromValues(rect.left, rect.top), matrix);
      vec2.transformMat2d(bR, vec2.fromValues(rect.right, rect.bottom), matrix);
      vec2.transformMat2d(tR, vec2.fromValues(rect.right, rect.top), matrix);
      vec2.transformMat2d(bL, vec2.fromValues(rect.left, rect.bottom), matrix);

      rect.top = Math.min(tL[1], tR[1], bR[1], bL[1]);
      rect.left = Math.min(tL[0], tR[0], bR[0], bL[0]);
      rect.bottom = Math.max(tL[1], tR[1], bR[1], bL[1]);
      rect.right = Math.max(tL[0], tR[0], bR[0], bL[0]);
    }

    rect.width = rect.right - rect.left;
    rect.height = rect.bottom - rect.top;
    rect.middle = rect.top + rect.height / 2;
    rect.center = rect.left + rect.width / 2;

    return rect;
  },

  _getNewRect() {
    return {
      top: null,
      bottom: null,
      left: null,
      right: null,
      height: null,
      width: null,
      middle: null,
      center: null,
    };
  },

  /**
   * Generates elementInfo containing current dimensionPositions and initialDimension positions.
   * Both set to the same values. Enforces minimums if appropriate.
   * @param {Map} elem - A full element map
   * @returns {Pick<ElementInfo, "w" | "iW" | "h" | "iH" | "x" | "iX" | "y" | "iY" | "a" | "iA">} - base element info
   */
  _getElementPosition(elem) {
    const layoutProps = gatherProps(elem, this.layout, ELEMENT_POSITION);

    // min height/width offsets
    const theme = ActiveBlockContainerStore.getContainer()?.get("theme");

    const minHeight = ElementUtils.getMinHeight(elem, this.layout, theme);
    const minWidth = ElementUtils.getMinWidth(elem, this.layout, theme);

    const baseHeight = layoutProps.get("h", 1);
    const baseWidth = layoutProps.get("w", 1);

    const w = baseWidth < minWidth ? minWidth : baseWidth;
    const h = baseHeight < minHeight ? minHeight : baseHeight;

    return {
      x: layoutProps.get("x"),
      y: layoutProps.get("y"),
      w,
      h,
      a: layoutProps.get("a"),
      iX: layoutProps.get("x"),
      iY: layoutProps.get("y"),
      iW: w,
      iH: h,
      iA: layoutProps.get("a"),
    };
  },

  /**
   * Collects and sets positions from selection to track and emit new positions during the transform.
   * Gathers container and id if elements are contained.
   * Collects child elements if selected element is a container.
   */
  initPositions() {
    this.positions = this.positions.clear();

    this.elements.map((data, id) => {
      const elemIdPath = IDUtils.getArray(id, 1);

      const elem = this.block.getIn(elemIdPath);
      const positionData = this._getElementPosition(elem);
      const isContainer = elem.get("type") === DataTypes.GROUP;

      // add parent dimensions and id if element is contained
      if (!!elem.get("pId")) {
        const containerId = elemIdPath.slice(0, -2);
        const container = this.block.getIn(containerId);
        positionData.container = gatherProps(
          container,
          this.layout,
          PROPERTY_MAPS.ELEMENT_POSITION,
          false
        )?.toJS();
        positionData.containerId = IDUtils.getArray(id).slice(0, -2).join(".");
      }

      // collect children if element is a container
      if (isContainer) {
        positionData.contained = elem
          .get("elements")
          .reduce((result, containedElement, containedId) => {
            const fullId = `${id}.${ELEMENTS}.${containedId}`;
            result[fullId] = this._getElementPosition(containedElement);
            return result;
          }, {});
      }

      this.positions = this.positions.set(id, positionData);
    });
  },

  getTransformableElements() {
    return this.elements.reduce((filteredElems, data, id) => {
      const elemIdPath = IDUtils.getArray(id, 1);
      const elem = this.block.getIn(elemIdPath);
      const locked = elem.get(AppConstants.LOCK_EDITING_KEY) === "1";
      const visible = [this.layout[0], "a"].includes(elem.get("visible"));
      return visible && !locked ? filteredElems.set(id, data) : filteredElems;
    }, Immutable.Map());
  },

  getTransformingStatus() {
    return this.transforming;
  },
});

export default TransformerStore;
